import { faClose, faFilter, faMinus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Datepicker from 'components/ui/Datepicker';
import Flex from 'components/ui/Flex';
import IconButton from 'components/ui/IconButton';
import Input from 'components/ui/Input';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import useDebounce from 'hooks/useDebounce';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { ReturnStatusType } from 'returnCenter-api';
import {
    FilterParams,
    returnThunks,
    SEARCH_MODE,
    SearchMode,
    selectSearchFilters,
    setFilterParams,
} from 'store/reducers/returnSlice';
import styled from 'styled-components';
import { getDateMonthsAgo } from 'utils/dates';

type ReturnFiltersProps = {
    pageSize: number;
    statusType?: ReturnStatusType;
    reset?: FilterParams;
};

const buildFilters = (q: FilterParams, statusType?: ReturnStatusType) => {
    const filtersObj: Record<string, unknown> = {};
    if (statusType != null) filtersObj.status = [statusType];
    if (q.searchMode === SEARCH_MODE.CUSTOMER_CODE && q.filterInput) filtersObj.customerCode = [q.filterInput];
    if (q.searchMode === SEARCH_MODE.CUSTOMER_GROUP && q.filterInput) filtersObj.customerGroupCode = [q.filterInput];
    return JSON.stringify(filtersObj);
};

const ReturnFilters = ({ pageSize, statusType, reset }: ReturnFiltersProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [isExpanded, setIsExpanded] = useState(false);

    const searchQuery = useAppSelector(selectSearchFilters);

    const searchModeOptions = [
        { value: SEARCH_MODE.NONE, label: t('search.select') },
        { value: SEARCH_MODE.CUSTOMER_CODE, label: t('search.customerCode') },
        { value: SEARCH_MODE.CUSTOMER_GROUP, label: t('search.customerGroup') },
    ];

    const handleSearch = useCallback(
        (updated?: FilterParams) => {
            const query = updated ?? searchQuery;
            const filters = buildFilters(query, statusType);

            dispatch(
                returnThunks.searchReturns({
                    returnOverviewRequest: {
                        page: 1,
                        size: pageSize,
                        filters,
                        startDate: query.from?.toISOString(),
                        endDate: query.to?.toISOString(),
                        search: query.query ?? '',
                        orderBy: query.orderBy,
                        orderByDirection: query.orderByDirection,
                    },
                }),
            );
        },
        [dispatch, searchQuery, statusType, pageSize],
    );

    const debouncedSearch = useDebounce(handleSearch, 350);

    const handleModeChange = useCallback(
        (mode: SearchMode) => {
            dispatch(
                setFilterParams({
                    ...searchQuery,
                    searchMode: mode,
                }),
            );
            if (searchQuery.filterInput) {
                debouncedSearch({ ...searchQuery, searchMode: mode });
            }
        },
        [searchQuery, dispatch, debouncedSearch],
    );

    const handleChange = useCallback(
        (partial: Partial<FilterParams>) => {
            const updated: FilterParams = { ...searchQuery, ...partial };
            dispatch(setFilterParams(updated));
            debouncedSearch(updated);
        },
        [searchQuery, dispatch, debouncedSearch],
    );

    const clearFilters = useCallback(() => {
        if (reset) {
            dispatch(setFilterParams(reset));
            debouncedSearch(reset);
        }
    }, [dispatch, debouncedSearch, reset]);

    return (
        <Wrapper gap="0.5rem" className="return-filters" data-allow-paste column>
            <TopRow gap="0.5rem">
                <Flex column $grow={1} gap="0.5rem" className="filter-free-text" justify="center">
                    <Flex column gap="4px" className="filter-search-free-text">
                        <Label>{t('search.search')}</Label>
                        <StyledInput
                            value={searchQuery.query}
                            placeholder={t('searchPlaceholder', 'Search by order number or return ID...')}
                            onChange={(e) => handleChange({ query: e.target.value })}
                        />
                    </Flex>
                </Flex>

                <Flex gap="4px" className="filter-date-range" column $wrap>
                    <Label>{t('created')}</Label>
                    <Flex gap="0.25rem" className="date-range-wrapper" align="center">
                        <DatePickerWrapper column>
                            <Datepicker
                                selectedDate={searchQuery?.from ?? getDateMonthsAgo(6)}
                                header={t('startDate')}
                                onChange={(date) => date instanceof Date && handleChange({ from: date })}
                                showIcon
                                filter={'None'}
                            />
                        </DatePickerWrapper>
                        <FontAwesomeIcon icon={faMinus} size="2xs" />
                        <DatePickerWrapper column>
                            <Datepicker
                                selectedDate={searchQuery?.to ?? new Date()}
                                header={t('endDate')}
                                onChange={(date) => date instanceof Date && handleChange({ to: date })}
                                showIcon
                                filter={'None'}
                            />
                        </DatePickerWrapper>
                        <VerticalDivider column align="center">
                            <StyledButton
                                className="cursor-pointer"
                                icon={faFilter}
                                onClick={() => setIsExpanded((prev) => !prev)}
                            >
                                {isExpanded ? t('lessFilters', 'Fewer filters') : t('moreFilters', 'More filters')}
                            </StyledButton>
                        </VerticalDivider>
                    </Flex>
                </Flex>
            </TopRow>

            {isExpanded && (
                <ExpandedSection gap="0.5rem" align="center" className="filter-search-customer-mode">
                    <Flex $grow={1} gap="0.5rem" align="end">
                        <Flex column gap="4px" $grow={1}>
                            <Label>{t('filterBy')}</Label>
                            <Flex gap="0.5rem" align="center">
                                <StyledSelect
                                    classNamePrefix="rs"
                                    placeholder={t('search.select', 'Select...')}
                                    value={searchModeOptions.find((option) => option.value === searchQuery.searchMode)}
                                    options={searchModeOptions}
                                    onChange={(selected) => {
                                        const { value } = selected as { value: SearchMode; label: string };
                                        handleModeChange(value);
                                    }}
                                />
                                <StyledInput
                                    disabled={searchQuery.searchMode === SEARCH_MODE.NONE}
                                    value={searchQuery.filterInput ?? ''}
                                    bgColor={searchQuery.searchMode === SEARCH_MODE.NONE ? 'disabled' : undefined}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (searchQuery.searchMode !== SEARCH_MODE.NONE) {
                                            handleChange({ filterInput: value });
                                        }
                                    }}
                                    placeholder={
                                        searchQuery.searchMode !== SEARCH_MODE.NONE
                                            ? t('search.searchBy', {
                                                  criteria:
                                                      searchQuery.searchMode === SEARCH_MODE.CUSTOMER_CODE
                                                          ? t('search.customerCode')
                                                          : t('search.customerGroup'),
                                              })
                                            : undefined
                                    }
                                />
                                <VerticalDivider column align="center">
                                    <StyledButton className="cursor-pointer" icon={faClose} onClick={clearFilters}>
                                        {t('clear')}
                                    </StyledButton>
                                </VerticalDivider>
                            </Flex>
                        </Flex>
                    </Flex>
                </ExpandedSection>
            )}
        </Wrapper>
    );
};

export default ReturnFilters;

const Wrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
    padding: 1rem 0.5rem 1.5rem 0.5rem;
`;

const TopRow = styled(Flex)`
    width: 100%;
    align-items: flex-end;
`;

const ExpandedSection = styled(Flex)`
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
    padding-top: 0.75rem;
`;

const Label = styled.span`
    font-size: 11px;
    color: ${({ theme }) => theme.colors.textLight};
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const DatePickerWrapper = styled(Flex)`
    .react-datepicker-wrapper,
    .react-datepicker__input-container {
        width: 100%;
    }

    .selected {
        background-color: ${({ theme }) => theme.colors.main} !important;
        color: #fff !important;
    }

    input {
        width: 100%;
        border: 1px solid ${({ theme }) => theme.colors.border} !important;
        border-radius: var(--radius);
        font-size: 15px !important;
        letter-spacing: 0.02em !important;
        font-weight: 500 !important;
        background-color: ${({ theme }) => theme.colors.fillWhite} !important;

        cursor: pointer;

        &:disabled {
            color: ${({ theme }) => theme.colors.textDisabled} !important;
            cursor: not-allowed !important;
        }
    }

    & * {
        color: ${({ theme }) => theme.colors.text} !important;
    }
`;

const StyledSelect = styled(Select)`
    min-width: 150px;
    & * {
        border-radius: 5px !important;
        margin: 0 !important;
        font-size: 14px !important;
    }

    .rs__menu-list {
        padding: 0 !important;
    }
`;

const VerticalDivider = styled(Flex)`
    border-left: 1px solid ${({ theme }) => theme.colors.border};
    margin-left: 0.5rem;
`;

const StyledButton = styled(IconButton)`
    height: 38px;
    padding: 0 1rem;
    border-radius: var(--radius);
    margin-left: 1rem;
`;

const StyledInput = styled(Input)`
    width: 100%;
    flex: 1;

    input {
        border: 1px solid ${({ theme }) => theme.colors.border} !important;
        border-radius: var(--radius) !important;
        padding: 0.5rem 0.5rem !important;
        font-size: 15px !important;
        letter-spacing: 0.02em !important;
        font-weight: 500 !important;
    }
`;
